import * as React from 'react'
import * as AppStore from './../../state/app-store'
import {
	Alert,
	Button,
	Checkbox,
	FileInput,
	Flex,
	Indicator,
	Loader,
	Notification,
	Tabs,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
	IconCameraPlus,
	IconCheck,
	IconPhoto,
	IconUpload,
	IconX,
} from '@tabler/icons-react'
import { useMutation, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import classNames from 'classnames'

import * as Types from '../../declarations'
import * as GQLQueries from '../../lib/queries'
import { getUploadThumbPath } from '../../lib/display'
import * as Selectors from './../../state/selectors'
// todo: remove dup import
import * as GQL from '../../lib/queries'

const ImageUpload = ({
	version,
	resourceId,
	resourceType,
}: {
	version: Types.App.DataTypes.TripVersion
	resourceId: number
	resourceType: Types.App.DataTypes.ResourceType
}) => {
	const router = useRouter()
	const { asPath } = router

	const slug = asPath.split('/').pop()

	const currentFeedbackEditSection = AppStore.useAppSelector(
		Selectors.currentFeedbackEditSection,
	)
	const isOpen = !!currentFeedbackEditSection
	const isPictureUploadOrTextFeedback =
		currentFeedbackEditSection === Types.App.DataTypes.FeedbackSection.PICTURES

	// todo: use isLoadingUploads in ui
	const {
		loading: isLoadingUploads,
		data: uploadsData,
		refetch: refetchMyUploads,
	} = useQuery<{
		getMyUploads: Types.API.UserUpload[]
	}>(GQL.Queries.GET_MY_UPLOADS, {
		variables: {
			tripSlug: slug,
		},
		skip: !isOpen || !isPictureUploadOrTextFeedback,
	})

	React.useEffect(() => {
		refetchMyUploads()
	}, [])

	const [myUploads, setMyUploads] = React.useState<Types.API.UserUpload[]>(
		uploadsData?.getMyUploads ?? [],
	)

	React.useEffect(() => {
		if (uploadsData?.getMyUploads) {
			setMyUploads(uploadsData.getMyUploads)
		}
	}, [uploadsData?.getMyUploads])

	const uploadForm = useForm({
		initialValues: {
			file: null,
			permission: false,
		},

		validate: {
			file: (value) => (value ? null : 'Please select a file'),
			permission: (value) =>
				value ? null : 'Must be a photo you have permission to upload',
		},
	})

	const [uploadFile, { loading: isProcessingUpload }] = useMutation<{
		uploadUserFile: Types.API.Response.UploadUserFile
	}>(GQLQueries.Mutations.UPLOAD_USER_FILE)

	const [
		fileUploadFeedbackSuccess,
		setFileUploadFeedbackSuccess,
	] = React.useState<boolean>(false)
	const [
		fileUploadFeedbackFailure,
		setFileUploadFeedbackFailure,
	] = React.useState<string>('')

	const uploadsAwaitApproval = myUploads.filter(({ isApproved }) => !isApproved)
		.length

	const [currentFileData, setCurrentFileData] = React.useState<string>('')

	React.useEffect(() => {
		const extension =
			// @ts-ignore
			uploadForm?.values?.file?.type?.split('/')?.[1]?.toLowerCase() ?? ''
		setCurrentFileData(extension)
	}, [uploadForm.values.file])

	const {
		// data,
		refetch: refetchSignedURL,
		loading: isFetchingSignedURL,
	} = useQuery<{
		getSignedURL: Types.API.Response.SignedURLResponse
	}>(GQL.Queries.GET_SIGNED_URL, {
		notifyOnNetworkStatusChange: true,
		variables: {
			currentFileData: currentFileData,
		},
		skip: currentFileData === '',
	})

	const [
		isPuttingImageToStorage,
		setIsPuttingImageToStorage,
	] = React.useState<boolean>(false)
	const isUploadingFile =
		isFetchingSignedURL || isPuttingImageToStorage || isProcessingUpload

	const isUploadFormInvalid = !uploadForm.isValid()
	const isUploadFormDisabled = isUploadingFile || isUploadFormInvalid

	const handleUploadSubmit = async (values: {
		file?: File | null
		permission: boolean
	}) => {
		try {
			if (!values.file) {
				console.warn('missing form data...', values)
				return
			}
			const file = Array.isArray(values.file) ? values.file[0] : values.file
			if (!(file instanceof File)) {
				console.error('The provided value is not a file:', file)
			}

			setFileUploadFeedbackFailure('')
			setFileUploadFeedbackSuccess(false)

			const fileType = file.type.split('/')[0]

			if (fileType !== 'image') {
				// todo: set error to alert (must be image)
				return
			}
			const extension = file.type.split('/')[1].toLowerCase()
			// todo: check image (jpeg or png)
			if (!['jpg', 'jpeg', 'png'].includes(extension)) {
				// todo: set error to alert (must be j)
				return
			}

			// 1/3 get upload url
			const resp = await refetchSignedURL({})
			const { signedURL, key } = resp.data.getSignedURL

			// 2/3 upload image to url
			setIsPuttingImageToStorage(true)
			const photoUploadResult: { status: number } = await fetch(signedURL, {
				method: 'PUT',
				headers: {
					'Content-Type': file.type,
				},
				body: file,
			})
			setIsPuttingImageToStorage(false)

			if (photoUploadResult.status !== 200) {
				// todo: display error to alert thing
				console.error('failed to upload')
				return
			}

			// 3/3 call our api to create the upload record and thumbs
			const response = await uploadFile({
				variables: {
					tripSlug: slug,
					storageKey: key,
					// @ts-ignore
					fileName: uploadForm.values?.file?.name ?? '',
					version,
					// todo: uniqueTripId
					// uniqueTripId,
					resourceId,
					resourceType,
				},
			})

			const success = response?.data?.uploadUserFile?.success ?? false
			const error = response?.data?.uploadUserFile?.error
			const userRefreshedUploads =
				response?.data?.uploadUserFile?.myUploads ?? []
			setMyUploads(userRefreshedUploads)

			if (success) {
				setFileUploadFeedbackSuccess(true)
			} else {
				setFileUploadFeedbackFailure(error || 'failed to upload')
			}

			if (success) {
				uploadForm.resetDirty()
				uploadForm.reset()
			}
		} catch (error) {
			console.error(error)
			setIsPuttingImageToStorage(false)

			if (error instanceof Error) {
				setFileUploadFeedbackFailure(error.message)
			}
		}
	}

	return (
		<>
			<Tabs variant="outline" defaultValue="upload">
				<Tabs.List>
					<Tabs.Tab value="upload" leftSection={<IconUpload size="0.8rem" />}>
						Upload
					</Tabs.Tab>
					<Tabs.Tab value="uploaded" leftSection={<IconPhoto size="0.8rem" />}>
						<Indicator
							inline
							label={isLoadingUploads ? '?' : myUploads.length}
							processing={isUploadingFile}
							size={16}
						>
							<span>Uploaded&nbsp;&nbsp;&nbsp;&nbsp;</span>
						</Indicator>
					</Tabs.Tab>
				</Tabs.List>

				<Tabs.Panel value="upload" pt="xs">
					<Alert
						icon={<IconCameraPlus size="1rem" />}
						title="Uploaded a picture to this page"
						color="blue"
					>
						Choose a high quality, and inspiring, picture of the route. It
						should be of a high quality and be relevant to the route.
					</Alert>
					<br />
					<form
						onSubmit={uploadForm.onSubmit((values) =>
							handleUploadSubmit(values),
						)}
					>
						<Flex
							gap="md"
							// justify="flex-start"
							direction="column"
						>
							<FileInput
								label="Upload Image"
								id="image-upload"
								// radius="md"
								// size="md"
								// multiple
								required
								accept="image/png,image/jpeg"
								leftSection={<IconUpload size={14} />}
								{...uploadForm.getInputProps('file')}
							/>

							<Checkbox
								label="I own or have permission to upload this picture"
								required
								{...uploadForm.getInputProps('permission', {
									type: 'checkbox',
								})}
							/>

							<div style={{ textAlign: 'right' }}>
								<Button type="submit" disabled={isUploadFormDisabled}>
									{isUploadingFile ? (
										<>
											Uploading&nbsp;&nbsp;&nbsp;
											<Loader size="sm" variant="dots" color="gray" />
										</>
									) : !isUploadFormInvalid ? (
										'Upload picture'
									) : (
										'Upload'
									)}
								</Button>
							</div>

							{fileUploadFeedbackSuccess && (
								<Notification
									icon={<IconCheck size="1.1rem" />}
									color="teal"
									title="Uploaded successfully"
									withCloseButton={false}
								>
									Thanks for your contribution. It will show on the site once
									approved.
								</Notification>
							)}

							{fileUploadFeedbackFailure !== '' && (
								<Notification
									icon={<IconX size="1.1rem" />}
									color="red"
									title="Failed to upload"
									withCloseButton={false}
								>
									{fileUploadFeedbackFailure}
								</Notification>
							)}
						</Flex>
					</form>
				</Tabs.Panel>

				<Tabs.Panel value="uploaded" pt="xs">
					{isLoadingUploads && (
						<>
							<Loader size="sm" variant="dots" color="gray" />
						</>
					)}
					{!isLoadingUploads && (
						<>
							<Alert
								icon={<IconCameraPlus size="1rem" />}
								title="Uploaded to this page already"
								color="blue"
							>
								{myUploads.length === 0
									? "You haven't uploaded anything to this page yet."
									: `So far you have uploaded ${myUploads.length} pictures.`}
								{uploadsAwaitApproval > 0 && (
									<>
										<br />
										<strong>
											{`${uploadsAwaitApproval} are awaiting approval`}
										</strong>
										&nbsp;before they can be used.
									</>
								)}
							</Alert>
							<br />
							<div className="user-uploads-to-page">
								{myUploads.map((pic, key) => (
									<div key={key} className="picture-container">
										<img
											key={key}
											src={getUploadThumbPath(pic.id, 'small')}
											className={classNames({
												'picture-user-uploaded': true,
												'is-not-approved': !pic.isApproved,
											})}
											{...(!pic.isApproved
												? { title: 'not approved yet' }
												: {})}
										/>
									</div>
								))}
							</div>
						</>
					)}
				</Tabs.Panel>
			</Tabs>
		</>
	)
}

export default ImageUpload
